import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Percent, Plus } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { formatDate } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { Tax, TaxAppliesTo, TaxType, TaxWrite } from "../api/types";

type Draft = {
  name: string;
  code: string;
  rate: string;
  tax_type: TaxType;
  applies_to: TaxAppliesTo;
  cgst_rate: string;
  sgst_rate: string;
  igst_rate: string;
  cess_rate: string;
  effective_from: string;
  effective_to: string;
  is_active: boolean;
  is_refundable: boolean;
};

function blankDraft(): Draft {
  return {
    name: "",
    code: "",
    rate: "0",
    tax_type: "exclusive",
    applies_to: "all",
    cgst_rate: "0",
    sgst_rate: "0",
    igst_rate: "0",
    cess_rate: "0",
    effective_from: "",
    effective_to: "",
    is_active: true,
    is_refundable: false,
  };
}

function draftFromTax(tax: Tax): Draft {
  return {
    name: tax.name,
    code: tax.code,
    rate: tax.rate ?? "0",
    tax_type: (tax.tax_type ?? "exclusive") as TaxType,
    applies_to: (tax.applies_to ?? "all") as TaxAppliesTo,
    cgst_rate: tax.cgst_rate ?? "0",
    sgst_rate: tax.sgst_rate ?? "0",
    igst_rate: tax.igst_rate ?? "0",
    cess_rate: tax.cess_rate ?? "0",
    effective_from: tax.effective_from ?? "",
    effective_to: tax.effective_to ?? "",
    is_active: tax.is_active ?? true,
    is_refundable: tax.is_refundable ?? false,
  };
}

function draftBody(draft: Draft): TaxWrite {
  return {
    name: draft.name.trim(),
    code: draft.code.trim(),
    rate: draft.rate.trim() || "0",
    tax_type: draft.tax_type,
    applies_to: draft.applies_to,
    cgst_rate: draft.cgst_rate.trim() || "0",
    sgst_rate: draft.sgst_rate.trim() || "0",
    igst_rate: draft.igst_rate.trim() || "0",
    cess_rate: draft.cess_rate.trim() || "0",
    effective_from: draft.effective_from || null,
    effective_to: draft.effective_to || null,
    is_active: draft.is_active,
    is_refundable: draft.is_refundable,
  };
}

/**
 * Tax configuration (FR-24.1).
 *
 * Rates are snapshotted onto invoice and credit-note lines, so editing a rate
 * here changes future documents only. The screen exposes the API the product
 * already had: list, create and edit. Deleting is intentionally not offered --
 * a tax that has been used is retired by clearing "active", not removed.
 */
export function TaxesPage() {
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Tax | null | undefined>(undefined);
  const canWrite = isManagerOrAbove(user?.role);

  const query = useQuery({
    queryKey: ["taxes"],
    queryFn: ({ signal }) => api.taxes(signal),
  });

  function close() {
    setEditing(undefined);
  }

  function saved() {
    close();
    void queryClient.invalidateQueries({ queryKey: ["taxes"] });
  }

  return (
    <>
      <PageHeader
        title="Taxes"
        subtitle="The rates available on invoices and credit notes. Rates are copied onto each document, so history never changes."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setEditing(null)}>
              <Plus size={14} /> Add tax
            </Button>
          ) : undefined
        }
      />

      {editing !== undefined ? (
        <TaxForm tax={editing} onSaved={saved} onCancel={close} />
      ) : null}

      <Card padded={false}>
        {query.isPending ? (
          <TableSkeleton rows={5} />
        ) : query.isError ? (
          <EmptyState
            title="Could not load taxes"
            body={query.error instanceof ApiError ? query.error.message : "Please try again."}
          />
        ) : (query.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<Percent size={28} />}
            title="No taxes configured"
            body="Add the GST or other rates your invoices should offer."
            action={
              canWrite ? (
                <Button variant="primary" onClick={() => setEditing(null)}>
                  <Plus size={14} /> Add the first tax
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[52rem]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Tax</th>
                  <th className="px-4 py-2.5">Code</th>
                  <th className="px-4 py-2.5 text-right">Rate</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Applies to</th>
                  <th className="px-4 py-2.5">Effective</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {query.data?.map((tax) => (
                  <tr key={tax.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{tax.name}</td>
                    <td className="px-4 py-3 text-xs font-medium uppercase text-slate-500">
                      {tax.code}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-900">
                      {Number(tax.rate ?? 0)}%
                    </td>
                    <td className="px-4 py-3 text-sm capitalize text-slate-600">
                      {tax.tax_type ?? "exclusive"}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize text-slate-600">
                      {tax.applies_to ?? "all"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {tax.effective_from && tax.effective_to
                        ? `${formatDate(tax.effective_from, timeZone)} – ${formatDate(tax.effective_to, timeZone)}`
                        : tax.effective_from
                          ? `From ${formatDate(tax.effective_from, timeZone)}`
                          : tax.effective_to
                            ? `Until ${formatDate(tax.effective_to, timeZone)}`
                            : "Always"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={tax.is_active === false ? "slate" : "emerald"}>
                        {tax.is_active === false ? "Inactive" : "Active"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canWrite ? (
                        <Button onClick={() => setEditing(tax)}>
                          <Pencil size={13} /> Edit
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function TaxForm({
  tax,
  onSaved,
  onCancel,
}: {
  tax: Tax | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(tax ? draftFromTax(tax) : blankDraft());
  const [localError, setLocalError] = useState<string | undefined>(undefined);

  const save = useMutation({
    mutationFn: () =>
      tax ? api.updateTax(tax.id, draftBody(draft)) : api.createTax(draftBody(draft)),
    onSuccess: onSaved,
  });

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft.name.trim() || !draft.code.trim()) {
      setLocalError("A tax needs both a name and a code.");
      return;
    }
    if (!Number.isFinite(Number(draft.rate)) || Number(draft.rate) < 0) {
      setLocalError("The rate must be zero or a positive number.");
      return;
    }
    setLocalError(undefined);
    save.mutate();
  }

  const error = localError ?? (save.error instanceof ApiError ? save.error.message : undefined);

  return (
    <Card className="mb-4">
      <h2 className="mb-1 text-sm font-semibold">{tax ? `Edit ${tax.name}` : "New tax"}</h2>
      <p className="mb-4 text-sm text-slate-500">
        A rate change never rewrites a past invoice: each line keeps the rate it was issued with.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Name">
            <input
              value={draft.name}
              onChange={(event) => update("name", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Code">
            <input
              value={draft.code}
              onChange={(event) => update("code", event.target.value)}
              placeholder="GST18"
              className={INPUT}
            />
          </Field>
          <Field label="Rate (%)">
            <input
              inputMode="decimal"
              value={draft.rate}
              onChange={(event) => update("rate", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Type">
            <select
              value={draft.tax_type}
              onChange={(event) => update("tax_type", event.target.value as TaxType)}
              className={INPUT}
            >
              <option value="exclusive">Exclusive</option>
              <option value="inclusive">Inclusive</option>
              <option value="none">None</option>
            </select>
          </Field>
          <Field label="Applies to">
            <select
              value={draft.applies_to}
              onChange={(event) => update("applies_to", event.target.value as TaxAppliesTo)}
              className={INPUT}
            >
              <option value="all">All</option>
              <option value="service">Services</option>
              <option value="product">Products</option>
            </select>
          </Field>
          <Field label="CGST (%)">
            <input
              inputMode="decimal"
              value={draft.cgst_rate}
              onChange={(event) => update("cgst_rate", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="SGST (%)">
            <input
              inputMode="decimal"
              value={draft.sgst_rate}
              onChange={(event) => update("sgst_rate", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="IGST (%)">
            <input
              inputMode="decimal"
              value={draft.igst_rate}
              onChange={(event) => update("igst_rate", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Cess (%)">
            <input
              inputMode="decimal"
              value={draft.cess_rate}
              onChange={(event) => update("cess_rate", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Effective from">
            <input
              type="date"
              value={draft.effective_from}
              onChange={(event) => update("effective_from", event.target.value)}
              className={INPUT}
            />
          </Field>
          <Field label="Effective to">
            <input
              type="date"
              value={draft.effective_to}
              onChange={(event) => update("effective_to", event.target.value)}
              className={INPUT}
            />
          </Field>
          <div className="flex flex-col justify-end gap-2">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(event) => update("is_active", event.target.checked)}
                className="size-4 rounded border-slate-300"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.is_refundable}
                onChange={(event) => update("is_refundable", event.target.checked)}
                className="size-4 rounded border-slate-300"
              />
              Refundable
            </label>
          </div>
        </div>

        {error ? (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : tax ? "Save tax" : "Create tax"}
          </Button>
          <Button onClick={onCancel} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

const INPUT =
  "w-full rounded-md border-0 bg-white px-2.5 py-1.5 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}
